/**
 * Drawing Tools Bar Component
 *
 * Shows drawing tools and current selection state
 */

import { LitElement, html, css } from 'lit'
import { customElement, property } from 'lit/decorators.js'

@customElement('drawing-tools-bar')
export class DrawingToolsBar extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      align-items: stretch;
      width: 100%;
      height: 100%;
    }

    .tools-group {
      display: flex;
      height: 100%;
    }

    .tool-button {
      width: 56px;
      height: 100%;
      background: none;
      border: none;
      color: rgba(255, 255, 255, 0.85);
      cursor: pointer;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 2px;
      transition: all 0.15s ease;
      padding: 0;
    }

    .tool-button:hover {
      color: white;
      background: rgba(255, 255, 255, 0.12);
    }

    .tool-button.selected {
      color: white;
      background: #5a7088;
    }

    .material-icons {
      font-family: 'Material Symbols Outlined';
      font-size: 22px;
    }

    .tool-label {
      font-size: 10px;
      font-weight: 500;
      line-height: 1;
    }
  `

  @property({ type: String })
  selectedTool?: string

  private selectTool(tool: string): void {
    // Toggle behavior - if same tool is selected, deselect it
    const newTool = this.selectedTool === tool ? null : tool

    this.dispatchEvent(
      new CustomEvent('tool-selected', {
        detail: { tool: newTool },
        bubbles: true,
      })
    )
  }

  override render(): unknown {
    return html`
      <div class="tools-group">
        <button
          class="tool-button ${this.selectedTool === 'point' ? 'selected' : ''}"
          @click=${(): void => this.selectTool('point')}
          title="Add point"
        >
          <span class="material-icons">location_on</span>
          <span class="tool-label">Point</span>
        </button>

        <button
          class="tool-button ${this.selectedTool === 'line' ? 'selected' : ''}"
          @click=${(): void => this.selectTool('line')}
          title="Draw line"
        >
          <span class="material-icons">show_chart</span>
          <span class="tool-label">Line</span>
        </button>

        <button
          class="tool-button ${this.selectedTool === 'polygon' ? 'selected' : ''}"
          @click=${(): void => this.selectTool('polygon')}
          title="Draw polygon"
        >
          <span class="material-icons">hexagon</span>
          <span class="tool-label">Polygon</span>
        </button>

        <button
          class="tool-button ${this.selectedTool === 'circle' ? 'selected' : ''}"
          @click=${(): void => this.selectTool('circle')}
          title="Draw circle"
        >
          <span class="material-icons">circle</span>
          <span class="tool-label">Circle</span>
        </button>

        <button
          class="tool-button ${this.selectedTool === 'rectangle' ? 'selected' : ''}"
          @click=${(): void => this.selectTool('rectangle')}
          title="Draw rectangle"
        >
          <span class="material-icons">rectangle</span>
          <span class="tool-label">Rect</span>
        </button>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'drawing-tools-bar': DrawingToolsBar
  }
}
